/**
 * EventPady+ — Onboarding Tour Context
 *
 * Manages tour state: fetching progress from the backend, stepping through
 * the tour, marking steps complete, and dismissing.
 *
 * The tour is shown automatically to ORG_OWNER and ORG_ADMIN users whose
 * organisation just became ACTIVE, until they complete or dismiss it.
 * SUPER_ADMIN users never see the tour.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { onboardingApi } from './api'
import type { OnboardingProgress, TourStepKey } from '@/types'

// ---------------------------------------------------------------------------
// Step metadata
// ---------------------------------------------------------------------------

export interface TourStep {
  key:         TourStepKey
  title:       string
  description: string
  /** Path to navigate to after completing this step (optional) */
  actionLabel?: string
  actionPath?:  string
}

export const TOUR_STEPS: TourStep[] = [
  {
    key:         'welcome',
    title:       'Welcome to EventPady+',
    description: "You're set up and ready to start capturing visitor information. This quick tour will show you the key features — it takes less than 2 minutes.",
    actionLabel: "Let's go",
  },
  {
    key:         'register_visitor',
    title:       'Register your first visitor',
    description: 'Head to the Visitors page and click "Register visitor" to capture your first walk-in. You can collect name, contact details, purpose, and even a photo.',
    actionLabel: 'Go to Visitors',
    actionPath:  '/visitors',
  },
  {
    key:         'configure_workflow',
    title:       'Configure visitor workflows',
    description: 'Workflows let you customise the registration form per visitor type — job applicants, deliveries, general guests. Set them up in Settings → Visitor Workflows.',
    actionLabel: 'Go to Workflows',
    actionPath:  '/settings/workflows',
  },
  {
    key:         'invite_team',
    title:       'Invite your team',
    description: 'Add staff members so they can register visitors and manage records. Invite them from Settings → Team Members.',
    actionLabel: 'Go to Team',
    actionPath:  '/settings/members',
  },
  {
    key:         'explore_sms',
    title:       'Send your first SMS',
    description: 'Use the SMS module to send individual messages or bulk campaigns to your visitor database. Explore templates and campaigns to get started.',
    actionLabel: 'Go to SMS',
    actionPath:  '/sms',
  },
]

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

interface TourContextValue {
  /** True while the initial progress fetch is in flight */
  isLoading:       boolean
  /** True if the tour overlay should be shown */
  isVisible:       boolean
  /** Resolved progress from the backend (null until loaded) */
  progress:        OnboardingProgress | null
  /** Index of the currently displayed step (0-based) */
  currentIndex:    number
  currentStep:     TourStep | null
  isLastStep:      boolean
  /** Mark current step complete and advance */
  completeAndNext: () => Promise<void>
  /** Jump directly to a step by key */
  goToStep:        (key: TourStepKey) => void
  /** Dismiss the tour without completing */
  dismiss:         () => Promise<void>
}

const TourContext = createContext<TourContextValue | null>(null)

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used within <TourProvider>')
  return ctx
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface TourProviderProps {
  children:     ReactNode
  /** Whether the current user should see the tour (pass false for SUPER_ADMIN) */
  tourEligible: boolean
}

export function TourProvider({ children, tourEligible }: TourProviderProps) {
  const [isLoading, setIsLoading]     = useState(true)
  const [progress, setProgress]       = useState<OnboardingProgress | null>(null)
  const [currentIndex, setCurrentIdx] = useState(0)

  // ── Fetch progress on mount ───────────────────────────────────────────────
  useEffect(() => {
    if (!tourEligible) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    onboardingApi.getProgress()
      .then(p => { if (!cancelled) setProgress(p) })
      .catch(() => { /* silent — tour won't show */ })
      .finally(() => { if (!cancelled) setIsLoading(false) })

    return () => { cancelled = true }
  }, [tourEligible])

  // ── Derived ───────────────────────────────────────────────────────────────
  const isVisible = Boolean(
    tourEligible &&
    !isLoading &&
    progress &&
    !progress.is_complete &&
    !progress.is_dismissed,
  )

  const currentStep = TOUR_STEPS[currentIndex] ?? null
  const isLastStep  = currentIndex === TOUR_STEPS.length - 1

  // ── Actions ───────────────────────────────────────────────────────────────
  const completeAndNext = useCallback(async () => {
    if (!currentStep) return
    try {
      const updated = await onboardingApi.completeStep(currentStep.key)
      setProgress(updated)
    } catch {
      // Optimistically advance even if the API call fails
    }
    if (isLastStep) return   // overlay hides via progress.is_complete
    setCurrentIdx(i => i + 1)
  }, [currentStep, isLastStep])

  const goToStep = useCallback((key: TourStepKey) => {
    const idx = TOUR_STEPS.findIndex(s => s.key === key)
    if (idx >= 0) setCurrentIdx(idx)
  }, [])

  const dismiss = useCallback(async () => {
    try {
      await onboardingApi.dismiss()
    } catch {
      // ignore
    }
    setProgress(prev => prev ? { ...prev, is_dismissed: true } : prev)
  }, [])

  const value = useMemo<TourContextValue>(
    () => ({
      isLoading,
      isVisible,
      progress,
      currentIndex,
      currentStep,
      isLastStep,
      completeAndNext,
      goToStep,
      dismiss,
    }),
    [isLoading, isVisible, progress, currentIndex, currentStep, isLastStep, completeAndNext, goToStep, dismiss],
  )

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}
